/**
 * 경제 지표 표시용 순수 함수 모음.
 * - 전부 primitive 입력 → 출력(DTO/네트워크/i18n 의존 없음, `formatIndicatorValue`만 예외 —
 *   로케일 표기 자체가 값 포맷의 일부라 `@shared` 로케일 유틸을 직접 쓴다. TRAD-3029: THOUSAND_PERSONS 의 KO « 명»
 *   접미사도 같은 이유로 카탈로그 t() 를 쓴다 — 단위 문구가 값 표기의 일부).
 * - 사용자 노출 라벨(카테고리명/출처명/지표명 등)은 여기서 다루지 않는다 — i18n 카탈로그(t()) 경유.
 */
import { formatKrwAmount, formatLocalizedDate, formatLocalizedNumber, getIntlLocale } from '@shared/lib';

import type { ApiLocalDate, IndicatorDeltaDto, IndicatorUnitCode } from '../types';
import { t } from '@/i18n';
import { signOf } from '@/shared/lib/utils/price-tone';
import { defineServerEnum } from '@/shared/lib/utils/server-enum';

const ISO_LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * y/m/d 각 성분으로 로컬 `Date`를 만들되, 존재하지 않는 날짜(예: 2월 30일)는 `null`로 거른다.
 * `new Date(y, m-1, d)`는 범위를 벗어난 값을 다음 달/해로 조용히 롤오버한다(2월 30일 →
 * 3월 2일) — 롤오버가 일어났다는 건 원래 입력이 유효하지 않았다는 뜻이므로, 만들어진 값을
 * 되읽어 입력과 다르면 무효로 판정한다.
 */
const toValidLocalDateOrNull = (year: number, month: number, day: number): Date | null => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

/**
 * `yyyy-MM-dd` 문자열 → 로컬 캘린더 날짜.
 * 🔴 TRAD-2636 3단계 — `[year, month, day]` 배열 입력 지원을 제거했다. 운영(xtrading.kr)
 * curl로 이행 완료를 확인한 뒤(list 11/11·history 2시리즈 전부 문자열, 배열 0건) 배열
 * 분기를 지웠다 — 이제 배열이 오면(구버전 backend로의 롤백 등) **의도적으로 null**이 되어
 * 화면에서 조용히 사라지지 않고 "값 없음"으로 드러난다(`toEconChartPoints`가 건너뛴다).
 *
 * 🔴 UTC 파싱을 하지 않는다 — `LocalDate`는 애초에 타임존이 없는 순수 캘린더 값이라, 로컬
 * 생성자(`new Date(y, m-1, d)`)로 만들고 로컬 포맷으로 그대로 렌더하면 어느 타임존에서 봐도
 * 같은 연/월/일이 나온다(UTC 파싱 후 `timeZone:'UTC'`로 되돌리는 것과 결과가 같지만 더 직접적).
 *
 * 🔴 `yyyy-MM-dd`가 아니거나(배열 포함), 존재하지 않는 날짜면 `null`을 반환한다. 오늘
 * 날짜·epoch 등 그럴듯한 값으로 대체하지 않는다 — 그러면 화면에 틀린 날짜가 뜨고 아무도
 * 모른다. 호출부가 `null`을 명시적으로 "값 없음"으로 처리한다(IndicatorCard 참고).
 */
export const parseApiLocalDate = (input: ApiLocalDate): Date | null => {
  const match = ISO_LOCAL_DATE_PATTERN.exec(input);
  if (!match) return null;
  const [, yearStr, monthStr, dayStr] = match;
  return toValidLocalDateOrNull(Number(yearStr), Number(monthStr), Number(dayStr));
};

const OFFSET_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

/**
 * TRAD-2772 — «다음 발표일»로 표시할 날짜. 표시하면 안 되는 경우는 전부 `null`:
 *   - 값 없음(null/undefined/키 부재) — 대부분의 지표는 이게 정상이다
 *   - 형식이 계약(`yyyy-MM-dd'T'HH:mm:ss+09:00`)과 다름 — 그럴듯한 날짜로 대체하지 않는다
 *   - 이미 지난 시각 — «다음» 발표라고 말할 수 없다(`now` 는 주입: 순수 함수)
 * 🔴 날짜는 **서버가 적은 날짜 부분(앞 10자)** 을 그대로 쓴다 — 뷰어 시간대로 환산하면
 * `2026-10-27T00:00:00+09:00` 이 미국에서 10월 26일로 찍힌다. 발표일은 출처가 공표한 달력 날짜다.
 */
export const getNextReleaseDate = (nextReleaseAt: string | null | undefined, now: Date): Date | null => {
  if (!nextReleaseAt || !OFFSET_DATE_TIME_PATTERN.test(nextReleaseAt)) return null;
  const instant = Date.parse(nextReleaseAt);
  if (Number.isNaN(instant) || instant < now.getTime()) return null;
  return parseApiLocalDate(nextReleaseAt.slice(0, 10));
};

/** 차트용 시계열 포인트(과거→현재 ms 오름차순) — `entities/derivatives`의 `ChartPoint`와 같은 모양이라 그대로 재사용. */
export interface EconChartPoint {
  time: number;
  value: number;
}

/** 파싱 실패한 포인트는 조용히 건너뛴다(전체 차트를 막지 않는다) — 나머지 포인트는 정상 표시. */
export const toEconChartPoints = (points: ReadonlyArray<{ date: ApiLocalDate; value: number }>): EconChartPoint[] =>
  points.flatMap(p => {
    const date = parseApiLocalDate(p.date);
    return date ? [{ time: date.getTime(), value: p.value }] : [];
  });

/**
 * TRAD-2866 — 축약 표기에도 천단위 구분자를 넣는다(정본: iOS·android 와 같은 "4,186.2조원").
 * `notation:'compact'` 의 기본 그루핑은 `min2` 라 정수부 4자리("4186.2조", "9999억")에는 구분자가
 * 안 붙고 5자리부터 붙는다 — iOS(`NumberFormatter .decimal`)·android(`COMPACT_DECIMAL_FORMAT`)는
 * 4자리부터 넣어 web 만 갈렸다. `useGrouping: true`(= always)로 맞춘다.
 */
const COMPACT_GROUPED: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1, useGrouping: true };

/**
 * 지표 값을 unit에 맞게 포맷.
 *
 * - `BILLION_KRW`/`BILLION_USD`/`USD`: 원값(억/만 단위가 아니라 raw 통화값)으로 환산 후
 *   `Intl.NumberFormat`의 `notation:'compact'`로 축약 — ko-KR은 조/억, en-US는 T/B/M을
 *   **로케일이 알아서** 고른다(실측: 4,186,152.2 BILLION_KRW → ko "4,186.2조" / en "4,186.2T").
 *   기존 `formatKrwScaled`는 억/만 2단계뿐이라 M2 규모(수천조원)에서 "4,186조"가 아니라
 *   "41,861,522억원"처럼 억 단위로 뭉개진다 — 이 스케일 전용으로 새로 작성(Rule 1 사유).
 * - `USD` 표기는 통화 코드(ISO)라 `format.amount_with_unit`의 BTC/USDT 티커와 같은 취급으로
 *   리터럴 접미사를 쓴다(자연어 문구가 아니라 데이터 라벨 — 하드코딩 금지 규칙의 대상이 아님).
 */
export const formatIndicatorValue = (unit: IndicatorUnitCode, value: number): string => {
  switch (unit) {
    case 'PERCENT':
      return `${formatLocalizedNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    case 'KRW_PER_USD':
      return formatKrwAmount(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case 'BILLION_KRW':
      return formatKrwAmount(value * 1_000_000_000, COMPACT_GROUPED);
    case 'BILLION_USD':
      return `${formatLocalizedNumber(value * 1_000_000_000, COMPACT_GROUPED)} USD`;
    case 'USD':
      return `${formatLocalizedNumber(value, COMPACT_GROUPED)} USD`;
    case 'INDEX':
      return formatLocalizedNumber(value, { maximumFractionDigits: 1 });
    case 'THOUSAND_PERSONS':
      // TRAD-3029 D2(정본 288194563 §2-11 v12) — ~~천 명 단위 정수 그루핑(«159,526»)~~ 은 단위를 알 수 없었다.
      // 값×1,000 을 로케일 compact(소수 1자리·그루핑, 반올림 halfExpand) — KO «15.7만 명» · EN «157K»(접미사 없음, 카탈로그).
      return t('econ.unit.thousand_persons', { value: formatLocalizedNumber(value * 1_000, COMPACT_GROUPED) });
    default:
      return formatLocalizedNumber(value);
  }
};

/**
 * 델타(변화량) 포맷 — 부호 포함.
 *
 * `unit === 'INDICATOR_UNIT'`이면 서버 계약상 카드 자신의 unit을 따라야 한다(간접 지시,
 * `formatIndicatorValue` 위임). 현재 실데이터엔 이 분기가 없어(PERCENT/BASIS_POINT만 관측)
 * 계약 문서 기준으로만 구현 — 실측 전까지는 구조적 대비로 취급한다.
 */
export const formatIndicatorDelta = (delta: IndicatorDeltaDto, cardUnit: IndicatorUnitCode): string => {
  // 🔸 INDICATOR_UNIT 은 표시 자릿수를 formatIndicatorValue(카드 단위)가 정해 «반올림 0» 판정 자릿수를 여기서 모른다 —
  //    TRAD-3670 범위에서 종전 그대로 둔다(실데이터 미관측 분기). 정확한 0 은 여전히 부호 없음.
  if (delta.unit === 'INDICATOR_UNIT')
    return `${delta.value > 0 ? '+' : ''}${formatIndicatorValue(cardUnit, delta.value)}`;
  // TRAD-3670 — bp(0자리)·%(2자리)는 표시 자릿수로 반올림한 값이 0 이면 부호 없이. 숫자는 절댓값으로 포맷(Intl «-0» 회피).
  if (delta.unit === 'BASIS_POINT')
    return `${signOf(delta.value, 0)}${formatLocalizedNumber(Math.abs(delta.value), { maximumFractionDigits: 0 })}bp`;
  // TRAD-3690 후보⑥ — ~~나머지는 전부 «%»~~(서버가 단위를 더하면 그 값을 퍼센트로 단정했다) → PERCENT 만 «%», 모르는 단위는 단위 없이
  // 부호+숫자(2자리)만. 단위를 지어내지 않는다.
  if (DELTA_UNIT.isKnown(delta.unit))
    return `${signOf(delta.value, 2)}${formatLocalizedNumber(Math.abs(delta.value), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  return `${signOf(delta.value, 2)}${formatLocalizedNumber(Math.abs(delta.value), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** TRAD-3690 — 델타 단위(서버 `DeltaUnitCode`). 위 formatIndicatorDelta 가 INDICATOR_UNIT·BASIS_POINT 를 먼저 처리하고 여기 남는 아는 값은 PERCENT. */
const DELTA_UNIT = defineServerEnum(['PERCENT', 'BASIS_POINT', 'INDICATOR_UNIT'] as const);

/**
 * D(designer spec, 2026-08-26 정정판) — 차트 아래 기간 캡션의 정밀도 임계값(월 개수).
 *
 * 🔴 2026-08-26 최초안은 "span >= 90일"이었으나 designer가 이를 폐기했다 — **90일은
 * "차트를 안 그린다"를 판정하던 값(iOS 게이트)이고, 이 라벨은 "월 표기가 범위를
 * 표현하는가"를 묻는 다른 질문이다.** "3개월 미만"은 자기설명적이다 — 월 라벨이 2개
 * 이하면 애초에 범위를 표현하지 못한다(`2026.08 ~ 2026.08`처럼 라벨 두 조각이 같아져
 * "범위가 없다"로 읽힌다 — 하필 이 라벨을 만든 이유였던 환율 카드가 그 함정에 걸린다).
 */
const CHART_RANGE_MONTH_THRESHOLD_MONTHS = 3;

export type EconChartRangePrecision = 'day' | 'month';

export interface EconChartRangeBounds {
  start: Date;
  end: Date;
  precision: EconChartRangePrecision;
}

/**
 * 시작~끝이 걸친 **서로 다른 달의 개수**. 🔴 `Math.abs(endMonth - startMonth) + 1`로 쓰면
 * 연도가 다를 때 양방향으로 틀린다(2025-12 ~ 2026-01은 절댓값상 1로 나오지만 실제로는
 * 2개월이다) — 그래서 연도 차이를 달 개수에 반드시 반영한다(designer가 못 박은 산술).
 */
const countMonthsSpanned = (start: Date, end: Date): number =>
  (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;

/**
 * 차트에 **실제로 그려진** 데이터(요청한 range가 아니라)의 시작~끝 날짜와 표시 정밀도를 계산한다.
 *
 * TRAD-2879 — `range=1Y`를 요청해도 환율 카드처럼 실제로는 12~18일치만 오는 지표가 있다.
 * "이 차트가 언제부터 언제까지인가"에 답하는 게 목적이므로 요청 파라미터가 아니라 응답
 * 포인트 자체에서 계산한다.
 *
 * `points`가 시간순 정렬돼 있다는 것에 기대지 않고 min/max로 구한다(`toEconChartPoints`는
 * 입력 순서를 그대로 보존해 오름차순을 보장하지 않는다 — 호출부가 정렬하지 않은 응답을
 * 보내도 이 함수는 안전해야 한다).
 *
 * 2점 미만이면 `null` — 차트 자체를 그리지 않는 가드(`chartPoints.length > 1`, IndicatorCard)와
 * 정합시킨다(캡션만 따로 뜨고 차트가 없는 상태를 만들지 않는다).
 */
export const getEconChartRangeBounds = (points: ReadonlyArray<EconChartPoint>): EconChartRangeBounds | null => {
  if (points.length < 2) return null;
  const times = points.map(p => p.time);
  const start = new Date(Math.min(...times));
  const end = new Date(Math.max(...times));
  const monthsSpanned = countMonthsSpanned(start, end);
  return { start, end, precision: monthsSpanned >= CHART_RANGE_MONTH_THRESHOLD_MONTHS ? 'month' : 'day' };
};

export interface EconChartValueRange {
  min: number;
  max: number;
}

/**
 * §F(designer 확정, TRAD-2879 후속) — 차트 y축의 **실제 값 범위**(min/max).
 *
 * `DerivativesHistoryChart`의 `<YAxis domain={['auto','auto']}>`는 데이터의 min~max로
 * 축을 재조정한다(recharts 기본 동작) — 그래서 실제 진폭이 2.5%뿐인 환율도 화면 높이
 * 전체를 채워 "폭락"처럼 보인다(designer가 android 실화면에서 잡은 왜곡, iOS·android는
 * §F 값 범위 라벨로 공개한다). web도 같은 `domain=['auto','auto']`를 쓰므로 같은 왜곡을
 * 안고 있고, 라벨이 없으면 web 사용자만 그 왜곡을 못 본다 — 그래서 web도 필요하다.
 *
 * `getEconChartRangeBounds`와 같은 가드(2점 미만 → null)로 차트 자체를 안 그리는 상태와
 * 정합시킨다. 정렬에 기대지 않고 min/max로 구한다(값 배열 순서는 이미 시간순이지만, 이
 * 함수의 계약 자체는 순서에 기대지 않는 것이 안전하다).
 */
export const getEconChartValueRange = (points: ReadonlyArray<EconChartPoint>): EconChartValueRange | null => {
  if (points.length < 2) return null;
  const values = points.map(p => p.value);
  return { min: Math.min(...values), max: Math.max(...values) };
};

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * KO 표기는 `YYYY.MM.DD`/`YYYY.MM`를 숫자로 직접 조립한다 — `Intl`의 `'ko-KR'` 기본 출력은
 * 공백과 후행 마침표가 섞인 `"2026. 8. 9."`/`"2025. 9."`라 designer spec 예시(`"2026.08.09"`/
 * `"2025.09"`)와 다르다(node로 직접 확인, 2026-08-26). 그래서 이 자리만 Intl에 위임하지 않는다.
 */
const formatKoRangeBound = (d: Date, precision: EconChartRangePrecision): string =>
  precision === 'day'
    ? `${d.getFullYear()}.${pad2(d.getMonth() + 1)}.${pad2(d.getDate())}`
    : `${d.getFullYear()}.${pad2(d.getMonth() + 1)}`;

/**
 * EN 표기는 월 이름만 `Intl`(`'en-US'`)에 위임한다. `'en-US'` 리터럴은 `getIntlLocale()` 자체가
 * 쓰는 것과 같은 이미 승인된 패턴 — `locale-hardcode-guard` 가드는 `'ko'`/`'ko-KR'` 리터럴만
 * 막고 `'en-US'`는 대상이 아니다(가드 자체 주석 확인).
 */
const formatEnRangeBound = (d: Date, precision: EconChartRangePrecision, includeYear: boolean): string => {
  if (precision === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return d.toLocaleDateString(
    'en-US',
    includeYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' },
  );
};

export interface EconChartRangeLabel {
  start: string;
  end: string;
}

/**
 * `getEconChartRangeBounds` 결과를 화면 로케일(ko/en)에 맞는 두 문자열로 만든다.
 *
 * 🔴 여기서 `~`/`–` 구분자 리터럴을 만들지 않는다 — 최종 문구 조립은 호출부가 카탈로그
 * `econ.chart.range`(`t()`)로 한다(하드코딩 금지).
 *
 * - EN, 일 단위: 같은 해 안이면 시작일의 연도를 생략한다(spec 예시 `"Aug 9 – Aug 26, 2026"`).
 *   다른 해에 걸치면(3개월 미만 구간이 연말/연초를 가로지르는 경우, 예: 2025-12-31~2026-01-01)
 *   시작에도 연도를 붙인다.
 * - EN, 월 단위: 양쪽 다 항상 연도를 붙인다(spec 예시 `"Sep 2025 – Aug 2026"` — 생략하면
 *   어느 해인지 알 길이 없어 짧은 표기가 오히려 정보 손실이다).
 * - KO: 일/월 단위 모두 양쪽에 항상 연도를 붙인다(spec 예시 둘 다 양쪽에 연도가 있다).
 */
export const formatEconChartRangeLabel = (bounds: EconChartRangeBounds): EconChartRangeLabel => {
  if (getIntlLocale() === 'ko-KR') {
    return {
      start: formatKoRangeBound(bounds.start, bounds.precision),
      end: formatKoRangeBound(bounds.end, bounds.precision),
    };
  }
  const sameYear = bounds.start.getFullYear() === bounds.end.getFullYear();
  return {
    start: formatEnRangeBound(bounds.start, bounds.precision, !sameYear),
    end: formatEnRangeBound(bounds.end, bounds.precision, true),
  };
};

/**
 * E-1(designer spec, TRAD-2879 부속 결함) — 경제지표 차트 툴팁 전용 날짜 포매터.
 *
 * 경제지표 포인트는 **날짜만 있는 값**(자정 고정, `toEconChartPoints` 참고)인데
 * `DerivativesHistoryChart`의 기본 `formatTime`은 시:분을 항상 찍어 의미 없는
 * `오전 12:00`이 매 포인트에 붙어 있었다. `DerivativesHistoryChart`에 새로 추가한 옵셔널
 * `timeFormatter`로 이 포매터를 주입해 econ 카드에서만 날짜만 표시한다 — 파생 3곳
 * (FundingRate/OpenInterest/LongShort)은 `timeFormatter`를 넘기지 않으므로 기존
 * `formatTime`(시:분 포함) 그대로다(그 데이터는 시:분이 실제로 의미 있다 — 영향 없음).
 *
 * 앱 로케일(`getIntlLocale()`)을 쓰는 `formatLocalizedDate`(`@shared/lib`)에 위임한다 —
 * 브라우저 로케일이 아니라 사용자가 명시 선택한 앱 로케일을 따라야 하기 때문이다
 * (D의 `formatEconChartRangeLabel`과 달리 이 자리는 spec이 요구하는 특정 리터럴 표기가
 * 없어 `Intl` 기본 출력 그대로 써도 된다 — `formatLocalizedDate` 재사용이 맞다).
 */
export const formatEconChartTooltipDate = (ms: number): string =>
  formatLocalizedDate(new Date(ms), { month: 'short', day: 'numeric' });

/**
 * TRAD-2907(designer §G-2) — IndicatorCard 기준일(asOf) 표기. KO `"2026.07.31"` / EN
 * `"Jul 31, 2026"`. 근거: 같은 카드 위 §D 차트 기간 캡션과 표기를 맞춘다(세로로 ~40pt
 * 떨어진 두 캡션이 서로 다른 관례를 쓰면 한 화면 안에서 어긋난다).
 *
 * 🔴 **`formatLocalizedDate`(Intl 위임) 로는 이 표기가 안 나온다** — `Intl.DateTimeFormat`은
 * ko-KR에서 어떤 옵션 조합을도 `"2026. 07. 31."`(공백 + 끝점)을 내지 `"2026.07.31"`을 못
 * 낸다(node로 직접 확인). 그래서 위 `formatEconChartRangeLabel`이 이미 그랬듯 KO는 Intl에
 * 안 맡기고 수동 조립한다 — **같은 함수(`formatKoRangeBound`/`formatEnRangeBound`)를
 * 그대로 재사용**한다(새로 안 짓는다, day precision + EN은 연도 포함).
 *
 * 🔴 이 자리가 iOS `DateFormatter.dateStyle = .medium` 함정과 같은 종류다 — 로케일에 따라
 * 다른 문자열이 필요한데 **하나의 옵션 객체로 양쪽을 다 내려던 시도(`month:'short'` 등)가
 * KO에서 "2026년 7월 31일"을 냈다.** `getIntlLocale()` 분기로 KO/EN 코드 경로를 완전히
 * 분리해 한쪽을 고칠 때 다른 쪽이 같이 안 움직이게 한다.
 */
export const formatEconAsOfDate = (date: Date): string =>
  getIntlLocale() === 'ko-KR' ? formatKoRangeBound(date, 'day') : formatEnRangeBound(date, 'day', true);

/** 범위형 지표(미 FFR 목표범위) 여부 — valueLow/valueHigh가 둘 다 있을 때만 범위로 본다. */
export const isRangeValue = (valueLow: number | null, valueHigh: number | null): boolean =>
  valueLow != null && valueHigh != null;

/**
 * 범위 표시(미 FFR 목표범위 등) — "3.50–3.75%".
 * `PERCENT`는 단위를 끝에 한 번만 붙인다(현재 실데이터의 유일한 범위형 지표). 그 외 단위는
 * 실데이터 관측 전이라 각 값을 독립 포맷해 이어붙이는 보수적 fallback만 둔다.
 */
export const formatIndicatorRange = (unit: IndicatorUnitCode, low: number, high: number): string => {
  if (unit === 'PERCENT') {
    const fmt = (v: number) => formatLocalizedNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${fmt(low)}–${fmt(high)}%`;
  }
  return `${formatIndicatorValue(unit, low)}–${formatIndicatorValue(unit, high)}`;
};
